// Predefined prompts for quick selection
export const BASIC_RECOMMENDATION_PROMPTS = [
  {
    id: 'market_analysis',
    label: 'Market Analysis',
    abbreviation: 'Market',
    prompt: 'Please analyze the current market conditions and provide trading recommendations based on technical indicators, market sentiment, and risk-reward ratios.'
  },
  {
    id: 'quick_opportunities',
    label: 'Quick Opportunities',
    abbreviation: 'Quick',
    prompt: 'What is the best short-term trading opportunity available right now? Focus on high-probability setups with clear entry and exit points.'
  },
  {
    id: 'risk_assessment',
    label: 'Risk Assessment',
    abbreviation: 'Risk',
    prompt: 'Review my current portfolio risk exposure and recommend a new trade to optimize risk-reward ratios.'
  },
  {
    id: 'trend_analysis',
    label: 'Trend Analysis',
    abbreviation: 'Trends',
    prompt: 'Analyze the current market trends across major cryptocurrency pairs and identify the strongest momentum play.'
  },
  {
    id: 'contrarian_plays',
    label: 'Contrarian Opportunities',
    abbreviation: 'Contrarian',
    prompt: 'Look for contrarian trading opportunities where market sentiment may be overly bearish or bullish, creating reversal setups.'
  },
  {
    id: 'breakout_setups',
    label: 'Breakout Setups',
    abbreviation: 'Breakouts',
    prompt: 'Identify cryptocurrencies approaching key resistance or support levels that could lead to significant breakout moves.'
  }
];


export const TRADER_RECOMMENDATION_PROMPTS = [
  // ============================================
  // TREND FOLLOWING (BI-DIRECTIONAL)
  // ============================================
  {
    id: 'trend_pullback_long',
    label: 'Bullish Trend Pullback',
    abbreviation: 'BullPull',
    prompt: `Analyze market data to find high-probability LONG entries on pullbacks.

STRATEGY: Enter established uptrends during short-term corrections to value areas.

DATA ANALYSIS:
1. Market Scan:
   - trend_direction must be "BULLISH"
   - trend_strength > 60 (strong trend)
   - price_change_24h_pct > 0 (macro trend is green)
   - price_change_1h_pct < -0.5% (micro pullback occurring)
   - rsi_14 between 40-55 (cooling off, not overbought)

2. Portfolio Check (portfolio_state_tool):
   - Ensure current Long exposure is < 70% of total_value_usd.
   - Do not recommend assets already in open_positions.

TRADE CONSTRUCTION:
- Entry: Limit order at nearest_support or current_price if within 0.5% of support.
- Stop-loss: 1.5 * volatility_24h_pct below nearest_support.
- Target: nearest_resistance (Minimum R:R of 2.0).
- Sizing: Calculate position size such that risk (Entry - Stop) equals 1% of total_value_usd.

OUTPUT: make a single recommendation with exact execution levels in the format required by the save_recommendation function.`
  },
  {
    id: 'trend_pullback_short',
    label: 'Bearish Trend Rally',
    abbreviation: 'BearRally',
    prompt: `Analyze market data to find high-probability SHORT entries on relief rallies.

STRATEGY: Enter established downtrends when price rallies into resistance (selling the rip).

DATA ANALYSIS:
1. Market Scan:
   - trend_direction must be "BEARISH"
   - trend_strength > 60
   - price_change_24h_pct < 0
   - price_change_1h_pct > 0.5% (relief rally occurring)
   - rsi_14 between 45-60 (bouncing but staying bearish)

2. Portfolio Check (portfolio_state_tool):
   - Ensure current Short exposure is < 70% of total_value_usd.

TRADE CONSTRUCTION:
- Entry: Limit order at nearest_resistance or current_price if within 0.5% of resistance.
- Stop-loss: 1.5 * volatility_24h_pct above nearest_resistance.
- Target: nearest_support (Minimum R:R of 2.0).
- Sizing: Calculate position size such that risk equals 1% of total_value_usd.

OUTPUT: Recommend the top SHORT candidates with exact execution levels. Make a single recommendation with exact execution levels in the format required by the save_recommendation function.`
  },
  {
    id: 'momentum_breakout_bi',
    label: 'Volatility Breakout (Bi-Directional)',
    abbreviation: 'VolBreak',
    prompt: `Analyze market data for immediate volatility breakout setups (Long or Short).

STRATEGY: Enter assets breaking key levels with expanding volume and volatility.

DATA ANALYSIS:
1. Market Scan:
   - volatility_24h_pct > 5% (market is moving)
   - volume_trend = "INCREASING"
   - momentum_24h absolute value is high (top 20 percentile)

2. Logic Branch:
   - IF price > nearest_resistance AND trend_direction != "BEARISH": Signal LONG.
   - IF price < nearest_support AND trend_direction != "BULLISH": Signal SHORT.

3. Risk Filter:
   - Reject if rsi_14 > 75 (for Longs) or < 25 (for Shorts).

TRADE CONSTRUCTION:
- Entry: Market execution at current_price.
- Stop-loss: Tight stop. For Longs: 2% below breakout level. For Shorts: 2% above breakdown level.
- Target: Project the breakout magnitude (1.5x the previous range).
- Sizing: 0.5% Risk of total_value_usd (Lower sizing for breakout strategies due to false-break risk).

OUTPUT: Identify the single best breakout candidate. Clearly state direction (LONG/SHORT) and why the volume confirms the move. Make a single recommendation with exact execution levels in the format required by the save_recommendation function.`
  },

  // ============================================
  // RANGE / MEAN REVERSION
  // ============================================
  {
    id: 'range_fade',
    label: 'Range Fade (Buy Low/Sell High)',
    abbreviation: 'RngFade',
    prompt: `Find setups to trade a ranging market by fading extremes.

STRATEGY: Short resistance or Buy support in low-trend environments.

DATA ANALYSIS:
1. Market Scan:
   - trend_strength < 40 OR trend_direction = "NEUTRAL"
   - volatility_24h_pct < 5% (compressed)
   - nearest_support and nearest_resistance are well defined.

2. Setup Identification:
   - POTENTIAL SHORT: current_price is within 2% of nearest_resistance AND rsi_14 > 60.
   - POTENTIAL LONG: current_price is within 2% of nearest_support AND rsi_14 < 40.

3. Portfolio Check:
   - Prefer trades that balance the portfolio delta (e.g. if net long, prioritize Short setups).

TRADE CONSTRUCTION:
- Entry: Limit order at the level (Support for Long, Resistance for Short).
- Stop-loss: Just outside the range (1% buffer).
- Target: The opposite side of the range (mid-range for partial profit).
- Sizing: Standard size (1% risk).

OUTPUT: Recommend the best Range Fade trade recommendation. Make a single recommendation with exact execution levels in the format required by the save_recommendation function.`
  },
  {
    id: 'extreme_reversion',
    label: 'Statistical Mean Reversion',
    abbreviation: 'StatRev',
    prompt: `Identify statistical anomalies for counter-trend scalps.

STRATEGY: Fade extreme deviations from the mean (RSI extremes).

DATA ANALYSIS:
1. Long Setup (Oversold):
   - rsi_14 < 25
   - price_change_24h_pct < -10%
   - current_price is near nearest_support.
2. Short Setup (Overbought):
   - rsi_14 > 75
   - price_change_24h_pct > 10%
   - current_price is near nearest_resistance.

TRADE CONSTRUCTION:
- Entry: Scale in (3 orders starting at current price).
- Stop-loss: Wide stop (3 * volatility_24h_pct).
- Target: Reversion to RSI 50 or EMA 20 equivalent.
- Sizing: SMALL. Max 0.5% risk of total_value_usd.

OUTPUT: Recommend a single asset in extreme conditions. Make a single recommendation with exact execution levels in the format required by the save_recommendation function.`
  },

  // ============================================
  // PORTFOLIO MANAGEMENT & HEDGING
  // ============================================
  {
    id: 'delta_hedge',
    label: 'Portfolio Hedge Recommendation',
    abbreviation: 'Hedge',
    prompt: `Analyze the current portfolio state and recommend a hedge if necessary.

STRATEGY: Reduce net exposure if the portfolio is unbalanced against the market trend.

DATA ANALYSIS:
1. Portfolio Analysis (portfolio_state_tool):
   - Calculate Net Delta: Sum of (Position Size * Direction).
   - If Net Delta is heavily LONG (> 50% equity) but global market trend (BTC/ETH) is turning BEARISH.
   - If Net Delta is heavily SHORT but global market trend is turning BULLISH.

2. Recommendation Logic:
   - If Unbalanced Long + Bearish Market: Find the weakest asset (High RSI, Bearish Trend, below Support) to OPEN SHORT.
   - If Unbalanced Short + Bullish Market: Find the strongest asset to OPEN LONG.

OUTPUT: 
- If portfolio is balanced: "Portfolio is balanced. No hedge needed. Provide a HOLD recommendation."
- If hedge needed: Recommend ONE specific trade to neutralize delta. Explain the logic based on total_value_usd protection. Make a single recommendation with exact execution levels in the format required by the save_recommendation function.`
  },

  // ============================================
  // STRATEGIC OVERVIEW
  // ============================================
  {
    id: 'best_opportunity_any',
    label: 'Best Opportunistic Trade (Long/Short)',
    abbreviation: 'BestOpp',
    prompt: `Identify the single highest conviction trade available right now, regardless of direction.

PROCESS:
1. Scan all assets using market_data_tool.
2. Filter for Confluence:
   - Trend Alignment: trend_strength > 65.
   - Volume Confirmation: volume_trend = "INCREASING".
   - Risk/Reward: Distance to target > 2x Distance to invalidation.

3. Compare Strategies:
   - Is a Long Trend Pullback better than a Short Breakdown right now?
   - Compare "Trend Strength" vs "Volatility".

4. Portfolio Constraints:
   - Check available_balance_usd.
   - ensure calculating size does not exceed max_position_size_usd.

OUTPUT: Recommend exactly ONE trade (Long or Short).
- Provide the Thesis (Why this trade?)
- Provide the Data (RSI, Volatility, Volume)
- Provide the Execution (Entry, Stop, Target, Size in USD).
- Make a single recommendation with exact execution levels in the format required by the save_recommendation function.`
  }
];